import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientCreateRequest, ClientDetail, ClientListItem } from '@crm/shared';
import { useClientsList, useClientCreate } from '../clients/api';
import { extractApiError } from '../../lib/api';

type Props = {
  value: ClientDetail | ClientListItem | null;
  onChange: (client: ClientDetail | ClientListItem) => void;
  disabled?: boolean;
};

/**
 * Autocomplete po klientach + opcja inline "Dodaj nowego klienta", która
 * rozwija mini-form (imię, telefon, opcjonalnie nazwisko/email + zgody RODO).
 * Po sukcesie tworzy klienta przez `POST /clients` i ustawia go jako wybranego.
 */
export function ClientPicker({ value, onChange, disabled }: Props): ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Klik poza wrapperem zamyka dropdown.
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const listQuery = useClientsList({
    search: debounced.length >= 2 ? debounced : undefined,
    page: 1,
    pageSize: 8,
    includeAnonymized: 'false',
  });

  const items = listQuery.data?.items ?? [];

  function pick(item: ClientListItem): void {
    onChange(item);
    setSearch('');
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative">
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm">
          <div>
            <div className="font-medium text-zinc-900">
              {value.firstName} {value.lastName ?? ''}
            </div>
            <div className="text-xs text-zinc-500">{value.phone}</div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(null as unknown as ClientListItem)}
              className="text-xs text-brand-600 hover:underline"
            >
              {t('appointments.client.change')}
            </button>
          )}
        </div>
      ) : (
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t('appointments.client.searchPlaceholder') ?? ''}
          className="input"
          disabled={disabled}
        />
      )}

      {open && !value && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-auto rounded-lg border border-zinc-200 bg-white shadow-lg">
          {listQuery.isLoading ? (
            <div className="px-3 py-2 text-sm text-zinc-500">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-3 text-sm text-zinc-500">
              {t('appointments.client.noResults')}
            </div>
          ) : (
            items.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-brand-50"
              >
                <div className="font-medium text-zinc-900">
                  {c.firstName} {c.lastName ?? ''}
                </div>
                <div className="text-xs text-zinc-500">
                  {c.phone}
                  {c.email ? ` · ${c.email}` : ''}
                </div>
              </button>
            ))
          )}
          <div className="border-t border-zinc-200 p-2">
            <button
              type="button"
              onClick={() => {
                setShowCreate(true);
                setOpen(false);
              }}
              className="w-full rounded-md bg-brand-50 px-3 py-2 text-left text-sm font-medium text-brand-700 hover:bg-brand-100"
            >
              + {t('appointments.client.addNew')}
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <InlineCreateClient
          initialName={search}
          onCancel={() => setShowCreate(false)}
          onCreated={(c) => {
            setShowCreate(false);
            onChange(c);
          }}
        />
      )}
    </div>
  );
}

type InlineProps = {
  initialName: string;
  onCancel: () => void;
  onCreated: (c: ClientDetail) => void;
};

function InlineCreateClient({ initialName, onCancel, onCreated }: InlineProps): ReactNode {
  const { t } = useTranslation();
  const create = useClientCreate();

  const [firstName, setFirstName] = useState(initialName);
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [rodoConsent, setRodoConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setError(null);
    if (!firstName.trim() || !phone.trim()) {
      setError(t('appointments.client.requiredFirstNameAndPhone'));
      return;
    }
    try {
      const payload: ClientCreateRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim() || null,
        phone: phone.trim(),
        email: email.trim() || null,
        rodoConsent,
        marketingConsent,
      };
      const c = await create.mutateAsync(payload);
      onCreated(c);
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-brand-200 bg-brand-50/50 p-3">
      <div className="text-sm font-medium text-zinc-700">
        {t('appointments.client.newClientTitle')}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          className="input"
          placeholder={t('clients.form.firstName') ?? ''}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          className="input"
          placeholder={t('clients.form.lastName') ?? ''}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <input
          className="input col-span-2"
          placeholder={t('clients.form.phone') ?? ''}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <input
          className="input col-span-2"
          placeholder={t('clients.form.email') ?? ''}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-xs text-zinc-700">
        <input
          type="checkbox"
          checked={rodoConsent}
          onChange={(e) => setRodoConsent(e.target.checked)}
        />
        {t('clients.form.rodoConsent')}
      </label>
      <label className="flex items-center gap-2 text-xs text-zinc-700">
        <input
          type="checkbox"
          checked={marketingConsent}
          onChange={(e) => setMarketingConsent(e.target.checked)}
        />
        {t('clients.form.marketingConsent')}
      </label>
      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={create.isPending}
          className="btn-primary"
        >
          {create.isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
}
